import { useQuery } from '@apollo/client';
import { Public, Schedule } from '@mui/icons-material';
import { Box, Button, Chip } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { ListPagination } from '~/components/ListPagination';
import { ListActions, ListContent, ListHeader } from '~/components/PageLayout';
import { Count } from '~/components/count';
import { ConfirmDialog } from '~/components/dialogs/confirmation';
import { SearchBar } from '~/components/forms/search-bar';
import { ScheduledShowDialog } from '~/components/schedule/dialog';
import { PastSchedulesSwitch } from '~/components/schedule/past-switch';
import { useAppContext } from '~/contexts';
import type { ScheduleInput, ScheduledShowsOrderBy } from '~/generated/graphql';
import { useConfirmDialog } from '~/hooks/dialogs';
import { useOrderByParams } from '~/hooks/order-by';
import { usePaginationParams, useTotalCount } from '~/hooks/pagination';
import { assert } from '~/lib/assert';
import {
  useCreateScheduledShow,
  useDeleteScheduledShow,
  useRemoveDeviceFromScheduledShow,
  useUpdateScheduledShow,
} from '../../queries/schedule';
import {
  DeviceScheduledShowsDocument,
  type DeviceSchedule__Device as Device,
  type DeviceSchedule__ScheduledShow as ScheduledShow,
} from '../../queries/schedule.generated';
import { Table } from './schedule/components/table';

interface ScheduleListProps {
  device: Device;
}

export const ScheduleList = ({ device }: ScheduleListProps) => {
  const { t } = useTranslation();
  const { currentUser } = useAppContext();

  const [searchParams, setSearchParams] = useSearchParams();
  const search = searchParams.get('search')?.trim() ?? '';
  const includeExpired = searchParams.get('expired')?.trim().toLowerCase() === 'true';

  const { orderBy } = useOrderByParams<ScheduledShowsOrderBy>();
  const { handleOutOfBounds, page, perPage } = usePaginationParams();

  const listVariables = {
    includeExpired,
    page,
    perPage,
    search,
    ...(orderBy && { orderBy }),
  };

  const { data, loading } = useQuery(DeviceScheduledShowsDocument, {
    variables: { deviceId: device.id, ...listVariables },
    onCompleted: (data) => handleOutOfBounds(data.device?.scheduledShows.pageInfo),
  });

  const [newScheduledShowOpen, setScheduledShowOpen] = useState(false);

  const [confirmDelete, confirmDeleteProps] = useConfirmDialog();
  const [confirmRemoveDevice, confirmRemoveDeviceProps] = useConfirmDialog();

  const [createSchedule] = useCreateScheduledShow();
  const [deleteSchedule] = useDeleteScheduledShow();
  const [removeDevicesFromScheduledShow] = useRemoveDeviceFromScheduledShow();
  const [updateSchedule] = useUpdateScheduledShow();

  const deleteScheduledShow = async (scheduledShow: ScheduledShow) => {
    if (!(await confirmDelete())) return;
    void deleteSchedule({ variables: { input: { scheduledShowIds: [scheduledShow.id] } } });
  };

  const removeDeviceFromScheduledShow = async (scheduledShow: ScheduledShow) => {
    if (!(await confirmRemoveDevice())) return;
    await removeDevicesFromScheduledShow({
      variables: {
        input: { deviceIds: [device.id], scheduledShowId: scheduledShow.id },
        ...listVariables,
      },
    });
  };

  const updateScheduledShow = async (
    name: string | null,
    schedule: ScheduleInput,
    showId: number,
    scheduledShowId?: number,
  ) => {
    assert(scheduledShowId != null, 'no scheduled show');
    await updateSchedule({
      variables: {
        input: {
          id: scheduledShowId,
          patch: { name, showId, schedule },
        },
      },
    });
  };

  const createScheduledShow = async (
    name: string | undefined,
    schedule: ScheduleInput,
    showId: number,
  ) => {
    await createSchedule({
      variables: {
        input: { name, schedule, selected: { ids: [device.id] }, showId },
        ...listVariables,
      },
    });
  };

  const handleSwitchChange = (showPast: boolean) => {
    setSearchParams((x) => {
      x.set('expired', showPast.toString());
      x.delete('page');
      return x;
    });
  };

  const totalCount = useTotalCount(data?.device?.scheduledShows.pageInfo.nodesCount);

  return (
    <>
      <ListHeader>
        <ListActions>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <SearchBar
              onChange={(value) =>
                setSearchParams((params) => {
                  params.set('search', value);
                  params.set('page', '1');
                  return params;
                })
              }
              placeholder={t('pages.schedules.search_placeholder')}
              search={search}
            />
            <PastSchedulesSwitch
              onSwitchChange={handleSwitchChange}
              includeExpired={includeExpired}
            />
            {currentUser.admin && (
              <Chip
                icon={<Public fontSize="small" />}
                label={device.timeZone}
                size="small"
                variant="outlined"
              />
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Count selectedCount={0} totalCount={totalCount} thing="pages.schedules.thing_plural" />
            {device.canSchedule.value && (
              <Button
                onClick={() => setScheduledShowOpen(true)}
                startIcon={<Schedule />}
                variant="contained"
              >
                {t('pages.devices.show.schedule.schedule_show')}
              </Button>
            )}
          </Box>
        </ListActions>
      </ListHeader>
      <ListContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
            <Table
              currentScheduledShowId={device.currentScheduledShow?.id}
              loading={loading}
              onDelete={deleteScheduledShow}
              onRemoveDevice={removeDeviceFromScheduledShow}
              onSubmit={updateScheduledShow}
              schedules={data?.device?.scheduledShows.nodes ?? []}
            />
          </Box>
          <Box sx={{ flexShrink: 0 }}>
            <ListPagination totalCount={totalCount} />
          </Box>
        </Box>
      </ListContent>
      <ScheduledShowDialog
        onClose={() => setScheduledShowOpen(false)}
        onSubmit={createScheduledShow}
        open={newScheduledShowOpen}
      />
      <ConfirmDialog
        {...confirmDeleteProps}
        confirm={t('pages.devices.show.schedule.permanently_delete')}
        deleteConfirm
        prompt={t('pages.devices.show.schedule.delete_schedule_prompt')}
        title={t('pages.devices.show.schedule.delete_schedule')}
      />
      <ConfirmDialog
        {...confirmRemoveDeviceProps}
        confirm={t('common.actions.remove')}
        deleteConfirm
        prompt={t('common.dialogs.remove_device_prompt')}
        title={t('common.dialogs.remove_device')}
      />
    </>
  );
};
